import React, { useMemo } from "react"
import { Table, message, Tag, Typography } from "@mimber/ui-web"
import { useDonations } from "@mimber/client/mimber-organization"
import type { DonationDto } from "@mimber/schema"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import {
  formatTableDate,
  applyQueryParamsToColumns,
  getColumnDateRangeProps,
  getColumnSearchProps,
  handleTableChange,
} from "@mimber/ui-web"
import { formatCurrency } from "@/utils"
import type { DonationsTableProps } from "./DonationsTable.types"
import {
  DONATION_SEARCH_PLACEHOLDERS,
  DONATION_TYPE_DISPLAY,
} from "./DonationsTable.utils"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

export const DonationsTable: React.FunctionComponent<DonationsTableProps> = ({
  className,
  onRowClick,
  ...tableProps
}) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "donations",
      defaultQueryParams: {
        page: 1,
        limit: 20,
      },
    })

  const { data: response, isLoading, error } = useDonations(queryParams)

  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load donations: ${error.message}`)
    }
  }, [error])

  const columns: ColumnsType<DonationDto> = useMemo(
    () => [
      {
        title: "Donation ID",
        dataIndex: "uuid",
        key: "uuid",
        ...getColumnSearchProps(DONATION_SEARCH_PLACEHOLDERS.donationId),
        render: (uuid: string) => (
          <Text copyable code>
            {uuid}
          </Text>
        ),
      },
      {
        title: "Donated On",
        dataIndex: "donatedAt",
        key: "donatedAt",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        ...getColumnDateRangeProps("donatedAt"),
        render: (donatedAt: string | null) =>
          donatedAt ? (
            <Text type="secondary">{formatTableDate(donatedAt)}</Text>
          ) : (
            <Text type="secondary">Never</Text>
          ),
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        sorter: true,
        sortDirections: ["ascend", "descend"] as const,
        render: (amount: number) => <Text>{formatCurrency(amount)}</Text>,
      },
      {
        title: "Currency",
        dataIndex: "currency",
        key: "currency",
        ...getColumnSearchProps(DONATION_SEARCH_PLACEHOLDERS.currency),
      },
      {
        title: "Donor Type",
        dataIndex: "isGuest",
        key: "isGuest",
        render: (isGuest: boolean) => {
          const display = isGuest
            ? DONATION_TYPE_DISPLAY.guest
            : DONATION_TYPE_DISPLAY.member
          return <Tag color={display.color}>{display.label}</Tag>
        },
      },
    ],
    []
  )

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const onTableChange: TableProps<DonationDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }

    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total ?? 0,
    queryParams,
    resetQueryParams
  )

  return (
    <>
      <Table<DonationDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data ?? []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => onRowClick?.(record),
          style: onRowClick ? { cursor: "pointer" } : undefined,
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
